import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '@mui/material/styles';
import './CustomModal.css';

export const CustomModal = ({ show, onHide, children, size = 'sm', centered = true, className = '', ...rest }) => {
  const overlayRef = useRef(null);
  const theme = useTheme();
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(show);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
      // Trigger animation after render
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
        document.body.style.overflow = 'unset';
      }, 400); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [show]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onHide && onHide();
    }
  };

  const handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      onHide && onHide();
    }
  };

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [show, onHide]);

  if (!shouldRender) return null;

  // Clone children and pass onHide to Header components
  const childrenWithProps = React.Children.map(children, child => {
    if (React.isValidElement(child) && child.props && child.props.closeButton !== undefined) {
      return React.cloneElement(child, { onHide });
    }
    return child;
  });

  const headerGradient = theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`;

  const modalContent = (
    <div
      ref={overlayRef}
      className={`custom-modal-overlay ${isAnimating && !isClosing ? 'show' : ''} ${isClosing ? 'closing' : ''}`}
      onClick={handleOverlayClick}
      style={{
        '--modal-bg': theme.palette.background.paper,
        '--modal-text': theme.palette.text.primary,
        '--modal-border': theme.palette.divider,
        '--modal-header-bg': headerGradient,
      }}
    >
      <div className={`custom-modal size-${size} ${className}`} {...rest}>
        {childrenWithProps}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export const CustomModalHeader = ({ children, closeButton = false, onHide, sx = {} }) => {
  // Merge sx props with default styles, allowing sx to override
  return (
    <div
      className="custom-modal-header"
      style={sx}
    >
      <div className="custom-modal-title">{children}</div>
      {closeButton && (
        <button className="custom-modal-close" onClick={onHide} aria-label="Close">
          ×
        </button>
      )}
    </div>
  );
};

export const CustomModalTitle = ({ children, sx = {} }) => {
  return <div style={sx}>{children}</div>;
};

export const CustomModalBody = ({ children, className = '', sx = {} }) => {
  return (
    <div className={`custom-modal-body ${className}`} style={sx}>
      {children}
    </div>
  );
};

export const CustomModalFooter = ({ children, sx = {} }) => {
  return (
    <div className="custom-modal-footer" style={sx}>
      {children}
    </div>
  );
};

// Export as default with subcomponents for compatibility
const Modal = CustomModal;
Modal.Header = CustomModalHeader;
Modal.Title = CustomModalTitle;
Modal.Body = CustomModalBody;
Modal.Footer = CustomModalFooter;

export default Modal;
